/* eslint-disable no-unused-vars */
/* eslint-disable max-len */
// 域名相关处理工具库

// 需要与旧官网server/helpers/getDomains.js保持大致一致。注：getDomainInfos不一样。
import _ from 'lodash';

import logger from './logger';
import { MultiReplacer, relationMapCombination } from './utils/stringReplaceUtils';

// 后续挪到配置文件里
const commonConfig = {
  domainFormatConfig: {
    keepUnnecessaryDomainInUrl: true, // 同域名下是否保留域名，true为保留，false为去掉域名只保留相对域名
    defaultExtendReplaceOn: true, // 是否默认开启额外转换，转换的map在下面。用于文档等地方包含中国站域名漏改为国际站的情况
    extendReplaceMap: {
      'console.cloud.tencent.com': 'console.tencentcloud.com',
      'buy.cloud.tencent.com': 'buy.tencentcloud.com',
    },
    // 同等域名列表，其中每行第一个为原始域名，后面的为衍生域名
    sameDomainList: [
      ['www.tencentcloud.com', 'intl.cloud.tencent.com'],
      ['console.tencentcloud.com', 'console.intl.cloud.tencent.com'],
      ['buy.tencentcloud.com', 'buy.intl.cloud.tencent.com'],
    ],
  },
};

/**
 * @typedef {Object} DomainInfosType
 * @property {String} QCMAIN_HOST 官网域名
 * @property {String} QCCONSOLE_HOST 控制台域名
 * @property {String} QCLOUD_ROOT_HOST cookie域名
 * @property {String} QCBUY_HOST 购买页域名
 * @property {String} QCCONSOLE_CN_HOST 中国站控制台域名
 * @property {String} QCBUY_CN_HOST 中国站购买页域名
 * @property {String} CHINA_HOST 中国站官网域名
 * @author yuiffydong
 */

const BASE_DOMAINS = {
  QCMAIN_HOST: 'www.tencentcloud.com',
  QCLOUD_ROOT_HOST: 'cloud.tencent.com',
  QCCONSOLE_HOST: 'console.tencentcloud.com',
  QCCONSOLE_CN_HOST: 'console.cloud.tencent.com',
  QCBUY_HOST: 'buy.tencentcloud.com',
  QCBUY_CN_HOST: 'buy.cloud.tencent.com',
  CHINA_HOST: 'cloud.tencent.com', // 中国站域名。在国际站域名修改后会和国际站域名、cookie域名区分开，单独处理
};

const MAIN_BASE_DOMAINS = {
  QCMAIN_HOST: 'tencentcloud.com',
  QCMAIN_HOST_INTL: 'intl.cloud.tencent.com',
  QCMAIN_HOST_WEBSITE: 'tencentcloud.website',
};

export const MAIN_DOMAINS = {
  QCMAIN_HOST: 'www.tencentcloud.com',
  QCMAIN_HOST_INTL: 'intl.cloud.tencent.com',
  QCMAIN_HOST_WEBSITE: 'www.tencentcloud.website',
};

/**
 * 生成需要的各种相对域名（控制台、购买页等）
 * @param  {String} host 基础host
 * @return {DomainInfosType} 各类域名
 * @author yuiffydong
 */
export function getDomains(host) {
  // QCMAIN_HOST 当前域名，如www.tencentcloud.com/intl.cloud.tencent.com/dev.intl.cloud.tencent.com:3001
  const QCMAIN_HOST = host || BASE_DOMAINS.QCMAIN_HOST;
  // QCMAIN_ROOT_HOST 国际站根域名，用于拼接其他域名，不导出。去除首部dev/www和尾部端口号，以便本地调试也能正常使用。如tencentcloud.com/intl.cloud.tencent.com
  const QCMAIN_ROOT_HOST = QCMAIN_HOST.replace(/^(dev\.)?(www\.)?/, '').replace(/:\d+$/, '');
  const QCLOUD_ROOT_HOST = QCMAIN_ROOT_HOST.replace(/^intl\./, ''); // 根域名，用于cookie。如cloud.tencent.com/tencentcloud.com
  const { CHINA_HOST } = BASE_DOMAINS; // 中国站域名，用于跳转
  const QCCONSOLE_HOST = `console.${QCMAIN_ROOT_HOST}`; // 国际控制台域名。新域名是console.tencentcloud.com，旧域名的控制台叫console.intl.cloud.tencent.com
  const QCCONSOLE_CN_HOST = `console.${CHINA_HOST}`; // 中国站控制台域名，用于一些域名替换
  const QCBUY_HOST = `buy.${QCMAIN_ROOT_HOST}`; // 国际购买页域名
  const QCBUY_CN_HOST = `buy.${CHINA_HOST}`; // 中国站购买页域名

  return { QCMAIN_HOST, QCLOUD_ROOT_HOST, QCCONSOLE_HOST, QCCONSOLE_CN_HOST, QCBUY_HOST, QCBUY_CN_HOST, CHINA_HOST };
}

/**
 * 获取各种相对路径域名
 * @returns {DomainInfosType} 各类域名
 * @author yuiffydong
 */
export const getDomainInfos = () => {
  let domainInfos = {};
  // console.log('getDomainInfos will judge is browser or not', { proBro: process.browser });
  if (typeof window === 'undefined') {
    // domainInfos = _.get(process, 'domain.req.res.$domains') || {}; // 旧官网是用中间件保存在这，直接获取来用
    // TODO: 后端状态下获取域名
    domainInfos = getDomains(); // 获取默认域名。大仓此处暂时无法获取到当前域名。store.getDomains()会串请求
    // console.log('getDomainInfos on server-side', {
    //   domainInfos,
    // });
  } else if (process && process.browser === true) {
    // domainInfos = window.DOMAIN_LIST || {}; // 旧官网是用中间件保存在这，直接获取来用
    try {
      domainInfos = getDomains(window && window.location.host); // 前端下直接获取当前域名
    } catch (e) {
      logger.info('getDomainInfos get domain from window catch error', e);
    }
  } else {
    logger.info('useDomains can not get domain');
  }
  // console.log('domainInfos', { domainInfos, isClientContext, isServerContext });
  return domainInfos;
};

const parseUrl = (srcUrl) => {
  let protocal;
  let host;
  let path;

  if (srcUrl && /^((?:https?:)?\/\/)([^/]+)(\/.*)?$/.test(srcUrl.trim())) {
    protocal = RegExp.$1;
    host = RegExp.$2;
    path = RegExp.$3;
  } else {
    protocal = '';
    host = '';
    path = srcUrl;
  }

  const ret = {
    protocal,
    host,
    path,
  };

  // console.log('parseUrl', srcUrl, ret);

  return ret;
};

/**
 * 生成域名替换用的map，从key替换到value
 * @param {{domainInfos?: DomainInfosType, extendReplaceOn?: boolean}} options
 * domainInfos为传入域名，不传入会自动获取，extendReplaceOn为是否开启额外替换
 */
const makeReplaceMap = (options = {}) => {
  const domainInfos = _.get(options, 'domainInfos') || getDomainInfos();
  // 第一阶段：额外处理，如把中国站控制台链接console.cloud.tencent.com处理为国际站控制台链接console.intl.cloud.tencent.com
  let replaceMapStep1 = {};
  const extendMapOn = _.has(options, 'extendReplaceOn')
    ? options.extendReplaceOn
    : _.get(commonConfig, 'domainFormatConfig.defaultExtendReplaceOn') || false;
  if (extendMapOn) {
    replaceMapStep1 = _.get(commonConfig, 'domainFormatConfig.extendReplaceMap') || {};
  }

  // 第二阶段：把同等的数个域名转换为BASE_DOMAINS中的格式，方便后续根据将其根据BASE_DOMAINS替换为当前域名
  const replaceMapStep2 = {};
  const sameDomainList = _.get(commonConfig, 'domainFormatConfig.sameDomainList') || [];
  sameDomainList.forEach((domainArr) => {
    const baseDomain = domainArr && domainArr[0];
    if (baseDomain) domainArr.slice(1).forEach((oneDomain) => {
      replaceMapStep2[oneDomain] = baseDomain;
    });
  });

  // 第三阶段：主要阶段，把BASE_DOMAINS里的域名替换为当前域名的对应形式
  const replaceMapStep3 = {};
  Object.keys(BASE_DOMAINS).forEach((domainKey) => {
    if (domainInfos[domainKey]) replaceMapStep3[BASE_DOMAINS[domainKey]] = domainInfos[domainKey];
  });
  const result = relationMapCombination([replaceMapStep1, replaceMapStep2, replaceMapStep3]);
  // console.log('makeReplaceMap', { replaceMapStep1, replaceMapStep2, replaceMapStep3, result });
  return result;
};

/**
 * 设置。可以通过domainInfos设置指定各个域名，而不是用自动获取的域名。
 * @typedef {Object} HostRelativeOptionsType
 * @property {DomainInfosType?} domainInfos
 */

/**
 * 把基础的.com的host转换为根据当前域名的相对域名(.com.cn/.cn）
 * @param {String} host 基本域名，如intl.console.cloud.tencent.com
 * @param {HostRelativeOptionsType} options 设置{@link HostRelativeOptionsType}
 * @returns {String} 相对的域名，如intl.console.cloud.tencent.cn
 */
const hostRelative = (host, options = {}) => {
  const replaceMap = makeReplaceMap(options);
  const multiReplacer = new MultiReplacer(replaceMap);
  return multiReplacer.replaceExact(host);
};

/**
 * 将url转化为符合相应域名（比如当前域名是intl.cloud.tencent.cn，就要把console.intl.cloud.tencent.com转化为console.intl.cloud.tencent.cn）的url。
 * 用于后台数据中获取的链接。
 * @param  {String} srcUrl 需要被转换的url
 * @param {HostRelativeOptionsType} options 设置{@link HostRelativeOptionsType}
 * @return {String} 转换后的url
 * @author yuiffydong
 */
export const formatUrlDomain = (srcUrl, options) => {
  let { protocal, host, path } = parseUrl(srcUrl);
  const keepUnnecessaryDomainInUrl = _.get(commonConfig, 'domainFormatConfig.keepUnnecessaryDomainInUrl') || false;
  if (!keepUnnecessaryDomainInUrl && host === BASE_DOMAINS.QCMAIN_HOST) {
    // 如果是官网域名，则用相对路径，不带域名
    protocal = '';
    host = '';
    if (!path) path = '/'; // 如果只有域名没有path，则path写个斜杠，以便返回一个"/"相对路径的首页
  } else if (host) {
    // 非官网域名，则进行域名转换
    host = hostRelative(host, options);
  }
  const ret = `${protocal}${host}${path}` || srcUrl;
  // console.log('formatUrlDomain', srcUrl, {protocal, host, path, ret});
  return ret;
};

export const isForeignLink = (redirectUrl) => {
  if (!redirectUrl) return false;
  const { host } = parseUrl(redirectUrl);
  return !Object.values(BASE_DOMAINS).includes(host);
};

/**
 * 对整篇文本中的链接的域名进行处理，把基础的.com的host转换为根据当前域名的相对域名(.com.cn/.cn）
 * @param {String} host 原文本
 * @param {HostRelativeOptionsType} options 设置{@link HostRelativeOptionsType}
 * @returns {String} 处理后文本
 */
export const hostRelativeReplaceAll = (text, options = {}) => {
  const replaceMapBase = makeReplaceMap(options);
  const replaceMap = {};
  Object.keys(replaceMapBase).forEach((replaceKey) => {
    // 增加双斜杠，使其只替换带有双斜杠的网址，尽量避免替换到非链接的地方
    if (replaceMapBase[replaceKey]) replaceMap[`//${replaceKey}`] = `//${replaceMapBase[replaceKey]}`;
  });
  const multiReplacer = new MultiReplacer(replaceMap);
  return multiReplacer.replace(text);
};

/**
 * 判断是否允许某域名下的跨域接口访问。比如控制台console.tencentcloud.com会调用官网的接口。
 * @param {String} domain 来源域名
 * @returns {boolean} 是否允许
 */
export const accessControlDomainTest = (domain) => /(cloud\.tencent\.com)|(cloud\.tencent\.cn)|(cloud\.tencent\.com\.cn)|(tencentcloud.com)$/.test(domain);

/**
 * 处理主备域名同频的 URL 转换
 * 网站有三个主备域名：www.tencentcloud.com、intl.cloud.tencent.com、www.tencentcloud.website
 * 当在某个域名访问时，将其他域名系统的链接转换为当前域名系统
 * @param {String} url 输入的 URL
 * @param {String} currentDomain 当前访问的域名（如 www.tencentcloud.website），不提供则从 window.location.host 获取
 * @return {String} 转换后的 URL
 * @example
 * // 在 https://www.tencentcloud.website/home 访问
 * // 输入：https://intl.cloud.tencent.com/products/cvm
 * // 输出：https://www.tencentcloud.website/products/cvm
 * // 输入：https://console.intl.cloud.tencent.com/cvm
 * // 输出：https://console.tencentcloud.website/cvm
 * adaptiveDomainUrl('https://console.intl.cloud.tencent.com/cvm', 'www.tencentcloud.website')
 */
export const adaptiveDomainUrl = (url, currentDomain) => {
  // 1. 早期返回：处理不需要转换的情况
  if (!url) return url;

  // javascript: 协议直接返回
  if (url.includes('javascript:')) return url;

  // 相对路径直接返回
  if (!/^(https?:)?\/\//.test(url)) {
    return url;
  }

  try {
    // 2. 获取当前域名（用于确定目标域名系统）
    let targetDomain = currentDomain;
    if (!targetDomain && typeof window !== 'undefined') {
      targetDomain = window.location.host;
    }
    if (!targetDomain) {
      targetDomain = BASE_DOMAINS.QCMAIN_HOST; // 默认域名
    }

    // 3. 定义三个主备域名系统
    const domainSystems = {
      tencentcloud_com: {
        main: `www.${MAIN_BASE_DOMAINS.QCMAIN_HOST}`,
        console: `console.${MAIN_BASE_DOMAINS.QCMAIN_HOST}`,
        buy: `buy.${MAIN_BASE_DOMAINS.QCMAIN_HOST}`,
      },
      intl_cloud_tencent_com: {
        main: `${MAIN_BASE_DOMAINS.QCMAIN_HOST_INTL}`,
        console: `console.${MAIN_BASE_DOMAINS.QCMAIN_HOST_INTL}`,
        buy: `buy.${MAIN_BASE_DOMAINS.QCMAIN_HOST_INTL}`,
      },
      tencentcloud_website: {
        main: `www.${MAIN_BASE_DOMAINS.QCMAIN_HOST_WEBSITE}`,
        console: `console.${MAIN_BASE_DOMAINS.QCMAIN_HOST_WEBSITE}`,
        buy: `buy.${MAIN_BASE_DOMAINS.QCMAIN_HOST_WEBSITE}`,
      },
    };

    // 4. 判断当前域名属于哪个系统
    let currentSystem = null;
    const targetDomainWithoutPort = targetDomain.replace(/:\d+$/, '');

    for (const [systemKey, domainSet] of Object.entries(domainSystems)) {
      if (Object.values(domainSet).some((d) => targetDomainWithoutPort.includes(d) || d.includes(targetDomainWithoutPort))) {
        currentSystem = domainSet;
        break;
      }
    }
    // 如果未识别，默认使用 tencentcloud.com 系统
    if (!currentSystem) {
      currentSystem = domainSystems.tencentcloud_com;
    }

    // 5. 解析 URL
    const { protocal, host, path } = parseUrl(url);
    if (!host) return url; // 无法解析的 URL 直接返回

    // 6. 判断 URL 中的域名属于哪个系统，并转换
    const hostWithoutPort = host.replace(/:\d+$/, '');
    let targetHost = host;

    // 提取原始 URL 中的端口号
    const portMatch = host.match(/:(\d+)$/);
    const urlPort = portMatch ? `:${portMatch[1]}` : '';

    // 收集所有可能的域名匹配项，按长度降序排列（避免 intl.cloud.tencent.com 误匹配 cloud.tencent.com）
    const allDomains = [];
    for (const [systemKey, domainSet] of Object.entries(domainSystems)) {
      for (const [type, domain] of Object.entries(domainSet)) {
        allDomains.push({ type, domain, domainSet });
      }
    }
    allDomains.sort((a, b) => b.domain.length - a.domain.length);

    // 逐个检查所有域名系统，找到匹配的域名
    let matched = false;
    for (const item of allDomains) {
      const { type, domain } = item;
      // 精确匹配：完全相等或以 .domain 结尾（如 console.intl.cloud.tencent.com 匹配 intl.cloud.tencent.com）
      if (hostWithoutPort === domain || hostWithoutPort.endsWith(`.${domain}`)) {
        // 找到了源域名，现在转换为目标系统的相应类型域名
        // 优先使用 URL 中的端口，如果没有则使用当前域名的端口
        const port = urlPort || (currentDomain.match(/:(\d+)$/) ? `:${currentDomain.match(/:(\d+)$/)[1]}` : '');
        targetHost = currentSystem[type] + port;
        matched = true;
        break;
      }
    }

    // 7. 拼接并返回转换后的 URL
    return `${protocal}${targetHost}${path}`;
  } catch (error) {
    logger.error('adaptiveDomainUrl error:', error, { url, currentDomain });
    return url; // 出错时返回原 URL
  }
};

/**
 * 将字符串中的所有 URL 的域名进行转换
 * @param {String} text 包含 URL 的文本内容
 * @param {String} currentDomain 当前访问的域名，不提供则从 window.location.host 获取
 * @return {String} 替换后的文本
 * @example
 * const text = '访问 https://intl.cloud.tencent.com/products/cvm 或 https://console.intl.cloud.tencent.com/cvm';
 * replaceDomainsInText(text, 'www.tencentcloud.website')
 * // 返回：'访问 https://www.tencentcloud.website/products/cvm 或 https://console.tencentcloud.website/cvm'
 */
export const replaceDomainsInText = (text, currentDomain) => {
  if (!text || typeof text !== 'string') return text;

  // 匹配 URL 的正则：http(s):// 或 // 开头，直到空格、换行、引号、尖括号或字符串结尾
  const urlRegex = /((?:https?:)?\/\/[^\s"'<>]+)/g;

  try {
    return text.replace(urlRegex, (match) => {
      return adaptiveDomainUrl(match, currentDomain);
    });
  } catch (error) {
    logger.error('replaceDomainsInText error:', error, { text, currentDomain });
    return text;
  }
};
